<template>
  <div class="verify-form">
    <div class="verify-form__header">
      <h3 class="verify-form__title">{{ t('page.verify-code-title') }}</h3>
      <p class="verify-form__subtitle">
        {{ t('page.code-sent-to') }} <strong>{{ email }}</strong>
      </p>
    </div>

    <AuthForm
        class="verify-form__code-input"
        :model="form"
        layout="vertical"
    >
      <a-form-item
          :validate-status="hasError ? 'error' : undefined"
          :help="hasError ? t('error.default-error-title') : undefined"
      >
        <a-input
            ref="codeInputRef"
            v-model:value="form.code"
            :maxlength="6"
            size="large"
            placeholder="000000"
            :status="hasError ? 'error' : undefined"
            :disabled="loading"
            @update:value="handleCodeChange"
            @paste="handlePaste"
        />
      </a-form-item>
    </AuthForm>

    <div class="verify-form__actions">
      <a-button
          type="link"
          class="verify-form__resend-button"
          :disabled="loading"
          @click="handleResendCode"
      >
        {{ t('page.try-again') }}
      </a-button>
    </div>

    <div class="verify-form__help">
      <TextView variant="roboto_14_regular">
        {{ t('page.code-not-received') }}
        {{ t('page.or-check-spam') }}
      </TextView>
      <TextView variant="roboto_14_regular">
        {{ t('page.if-nothing-helps') }}
        <a-typography-link :href="`mailto:${supportEmail}`">
          {{ t('page.support') }}
        </a-typography-link>
      </TextView>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted } from 'vue'
import { AuthForm } from '@/ui/common/auth'
import TextView from '@/ui/common/text-view/TextView.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps<{
  email: string
  loading: boolean
  hasError: boolean
  initialCode?: string
}>()

const emit = defineEmits<{
  (e: 'submit', code: string): void
  (e: 'back'): void
  (e: 'resend'): void
}>()

const codeInputRef = ref()
const form = ref({
  code: '',
})

const supportEmail = 'support@motorplat.ru'

// Устанавливаем начальный код из пропсов
onMounted(() => {
  if (props.initialCode) {
    form.value.code = props.initialCode

    // Если код уже полный, автоматически отправляем
    if (form.value.code.length === 6 && !props.loading) {
      emit('submit', form.value.code)
    }
  }
})

// Следим за изменением initialCode
watch(() => props.initialCode, (newCode) => {
  if (newCode) {
    form.value.code = newCode

    // Если код полный и компонент не в состоянии загрузки, отправляем
    if (newCode.length === 6 && !props.loading) {
      nextTick(() => {
        emit('submit', newCode)
      })
    }
  }
})

const handleCodeChange = (value: string) => {
  // Очищаем от не-цифр и обрезаем
  const cleaned = value.replace(/\D/g, '')
  form.value.code = cleaned.slice(0, 6)

  if (form.value.code.length === 6 && !props.loading) {
    emit('submit', form.value.code)
  }
}

const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault()

  const pastedText = event.clipboardData?.getData('text')
  if (pastedText) {
    const digits = pastedText.replace(/\D/g, '').slice(0, 6)
    form.value.code = digits

    if (digits.length === 6) {
      nextTick(() => {
        emit('submit', form.value.code)
      })
    }
  }
}

const handleBack = () => {
  emit('back')
}

const handleResendCode = () => {
  if (props.loading) return
  emit('resend')
  form.value.code = ''
}

// Фокус на инпут при монтировании и изменении email
watch(() => props.email, () => {
  if (!props.initialCode) {
    form.value.code = ''
  }

  nextTick(() => {
    codeInputRef.value?.focus()
  })
}, { immediate: true })
</script>

<style lang="scss" scoped>
.verify-form {
  position: relative;
  padding-top: 20px;

  &__header {
    text-align: center;
    margin-bottom: 24px;
  }

  &__title {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 8px;
  }

  &__subtitle {
    font-size: 14px;
    color: var(--text-secondary);
  }

  &__code-input {
    margin-bottom: 8px;

    :deep(.ant-input) {
      text-align: center;
      font-size: 24px;
      letter-spacing: 8px;
      font-weight: 600;
    }
  }

  &__actions {
    display: flex;
    justify-content: center;
    margin-bottom: 24px;
  }

  &__resend-button {
    padding: 0;
  }

  &__help {
    margin-top: 24px;
    text-align: center;

    > * {
      margin-bottom: 8px;
    }
  }
}
</style>